/**
 * Fixed Assets Register (UI-18)
 *
 * Summary cards + DataTable + Add Asset dialog, on BE-18.
 * A row opens the asset: its depreciation schedule and, for the TC admin, Dispose / Void.
 * Run Depreciation (TC admin) previews every month not yet depreciated up to a chosen month, then posts one entry per month.
 * Route: /accounting/assets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, ListSubheader, Tooltip, Divider,
} from '@mui/material';
import { Add, FileDownload, Settings as SettingsIcon } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { SummaryCardGrid, DataTable, CurrencyText } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage, unwrapList } from '../../utils/apiConvert';
import ApiService from '../../services/api.service';
import { fixedAssetService } from '../../services/accounting/fixedAsset.service';
import { creditCardService } from '../../services/accounting/creditCard.service';
import { cardLabel } from './CreditCards';

const STATUS_COLORS = {
  ACTIVE: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  FULLY_DEPRECIATED: { bg: 'rgba(107,114,128,0.15)', color: '#6b7280' },
  DISPOSED: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
  VOIDED: { bg: 'rgba(107,114,128,0.15)', color: '#9ca3af' },
};
const STATUS_LABELS = { ACTIVE: 'ACTIVE', FULLY_DEPRECIATED: 'Depreciated', DISPOSED: 'DISPOSED', VOIDED: 'VOIDED' };
const MONEY_ACCOUNT_TYPES = ['OPERATIONS', 'CASH', 'E_WALLET'];

const money = (v) => (parseFloat(v) || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const newKey = () => `asset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const emptyForm = () => ({ name: '', category: '', purchaseDate: toYmd(new Date()), cost: '', description: '', paidFrom: '' });
const emptyDispose = () => ({ disposalMethod: 'SOLD', disposalDate: toYmd(new Date()), proceedsAmount: '', disposalAccountId: '', remarks: '' });

const StatusChip = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.ACTIVE;
  return <Chip label={STATUS_LABELS[status] || status} size="small" sx={{ bgcolor: s.bg, color: s.color, fontSize: '0.7rem', height: 22 }} />;
};

const Fact = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: fs.xs, fontWeight: fw.medium, textAlign: 'right' }}>{children}</Typography>
  </Box>
);

// The latest month a run can reach: last month, or this month on its last day (nothing is posted into the future).
const latestDepreciationMonth = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const d = now.getDate() === lastDay ? now : new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// '2026-09' → 'Sep 2026'.
const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const FixedAssets = ({ currentUser }) => {
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === 'TC_ADMIN';
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [threshold, setThreshold] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Add asset
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formKey, setFormKey] = useState(newKey);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Run depreciation
  const [depreciationDialogOpen, setDepreciationDialogOpen] = useState(false);
  const [depMonth, setDepMonth] = useState(latestDepreciationMonth());
  const [depreciationPreview, setDepreciationPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [depreciationRunning, setDepreciationRunning] = useState(false);

  // Asset detail
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [detailError, setDetailError] = useState(null);

  // Dispose / void
  const [disposeDialogOpen, setDisposeDialogOpen] = useState(false);
  const [disposeForm, setDisposeForm] = useState(emptyDispose);
  const [disposeError, setDisposeError] = useState(null);
  const [disposing, setDisposing] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState(null);
  const [voiding, setVoiding] = useState(false);

  const categoryLabel = (key) => categories.find((c) => c.key === key)?.label || key;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [assetsRes, summaryRes, configRes] = await Promise.all([
        fixedAssetService.getAll(),
        fixedAssetService.getSummary(),
        fixedAssetService.getConfig(),
      ]);
      setAssets(assetsRes?.data || []);
      setSummary(summaryRes?.data || null);
      setCategories(configRes?.data?.categories || []);
      setThreshold(configRes?.data?.capitalizationThreshold ?? null);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load assets'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    ApiService.getTcBankAccounts().then(unwrapList)
      .then((r) => setAccounts(r.items.filter((a) => a.isActive !== false && MONEY_ACCOUNT_TYPES.includes(a.accountType))))
      .catch(() => {});
    creditCardService.getCards().then((list) => setCards((list || []).filter((c) => c.active !== false))).catch(() => {});
  }, []);

  const accountName = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? [a.bankName, a.accountName].filter(Boolean).join(' — ') : '—';
  };
  const paidFromName = (asset) => {
    if (asset.paymentCardId) { const c = cards.find((x) => x.id === asset.paymentCardId); return c ? cardLabel(c) : 'Credit card'; }
    return accountName(asset.paymentAccountId);
  };

  const COLUMNS = [
    { key: 'name', label: 'Asset Name', width: '18%' },
    { key: 'category', label: 'Category', width: '10%', render: (r) => categoryLabel(r.category) },
    { key: 'purchaseDate', label: 'Purchased', width: '10%', render: (r) => formatDate(r.purchaseDate) },
    { key: 'cost', label: 'Cost', width: '11%', align: 'right', render: (r) => <CurrencyText amount={r.cost} /> },
    { key: 'monthlyDepreciation', label: 'Dep/mo', width: '10%', align: 'right', render: (r) => <CurrencyText amount={r.monthlyDepreciation} /> },
    { key: 'accumulatedDepreciation', label: 'Accum.', width: '11%', align: 'right', render: (r) => <CurrencyText amount={r.accumulatedDepreciation} /> },
    { key: 'bookValue', label: 'Book Value', width: '11%', align: 'right', render: (r) => <CurrencyText amount={r.bookValue} /> },
    { key: 'status', label: 'Status', width: '9%', render: (r) => <StatusChip status={r.status} /> },
  ];

  const summaryCards = [
    { label: 'Total Book Value', value: summary?.totalBookValue ?? 0, color: 'info' },
    { label: 'Monthly Depreciation', value: summary?.monthlyDepreciation ?? 0, color: 'warning' },
    { label: 'Fully Depreciated', value: summary?.fullyDepreciatedCount ?? 0, suffix: 'assets' },
  ];

  // ── Add asset ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm(emptyForm());
    setFormKey(newKey());
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.category || !form.cost || !form.paidFrom) return;
    setSaving(true);
    setFormError(null);
    try {
      const [kind, id] = form.paidFrom.split(':');
      await fixedAssetService.create({
        name: form.name.trim(),
        category: form.category,
        purchaseDate: form.purchaseDate,
        cost: form.cost,
        description: form.description.trim(),
        paymentAccountId: kind === 'account' ? id : undefined,
        paymentCardId: kind === 'card' ? id : undefined,
        idempotencyKey: formKey,
      });
      setDialogOpen(false);
      load();
    } catch (err) {
      setFormError(serverMessage(err, 'Failed to add the asset'));
    } finally {
      setSaving(false);
    }
  };

  // Depreciation preview in dialog
  const selectedCat = categories.find((c) => c.key === form.category);
  const previewMonthly = form.cost && selectedCat
    ? (parseFloat(form.cost) / (selectedCat.usefulLifeYears * 12)).toFixed(2)
    : null;
  const belowThreshold = threshold != null && form.cost !== '' && parseFloat(form.cost) <= parseFloat(threshold);

  // ── Run depreciation ───────────────────────────────────────────────────
  const loadPreview = useCallback(async (month) => {
    setPreviewLoading(true);
    setPreviewError(null);
    setDepreciationPreview(null);
    try {
      const res = await fixedAssetService.getDepreciationPreview(month);
      setDepreciationPreview(res?.data || null);
    } catch (err) {
      setPreviewError(serverMessage(err, 'Could not preview depreciation'));
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const openDepreciation = () => {
    const month = latestDepreciationMonth();
    setDepMonth(month);
    setDepreciationDialogOpen(true);
    loadPreview(month);
  };

  const postDepreciation = async () => {
    setDepreciationRunning(true);
    setPreviewError(null);
    try {
      const res = await fixedAssetService.runDepreciation({ upTo: depMonth });
      setDepreciationDialogOpen(false);
      setNotice(`Depreciation posted up to ${monthLabel(depMonth)}: ${res?.data?.monthsPosted ?? 0} monthly entr${res?.data?.monthsPosted === 1 ? 'y' : 'ies'}, `
        + `${res?.data?.assetsProcessed ?? 0} asset(s), AED ${money(res?.data?.totalDepreciation)}`);
      load();
    } catch (err) {
      setPreviewError(serverMessage(err, 'Depreciation was not posted'));
    } finally {
      setDepreciationRunning(false);
    }
  };

  const previewMonths = depreciationPreview?.months || [];
  const lockedMonths = previewMonths.filter((m) => m.locked);
  const previewRows = (depreciationPreview?.assets || []).map((p) => {
    const asset = assets.find((a) => a.id === p.assetId) || {};
    return { ...p, usefulLifeYears: asset.usefulLifeYears, bookAfter: Math.max(0, (parseFloat(asset.bookValue) || 0) - (parseFloat(p.depreciationAmount) || 0)) };
  });

  // ── Asset detail ───────────────────────────────────────────────────────
  const openDetail = async (asset) => {
    setSelectedAsset(asset);
    setSchedule(null);
    setDetailError(null);
    try {
      const res = await fixedAssetService.getDepreciationSchedule(asset.id);
      setSchedule(res?.data || null);
    } catch (err) {
      setDetailError(serverMessage(err, 'Could not load the schedule'));
    }
  };

  const inUse = selectedAsset && ['ACTIVE', 'FULLY_DEPRECIATED'].includes(selectedAsset.status);
  const nothingPosted = schedule && !(schedule.schedule || []).some((s) => s.posted);
  const canVoid = isAdmin && selectedAsset?.status === 'ACTIVE' && nothingPosted;

  const openDispose = () => {
    setDisposeForm(emptyDispose());
    setDisposeError(null);
    setDisposeDialogOpen(true);
  };

  const handleDispose = async () => {
    setDisposing(true);
    setDisposeError(null);
    try {
      const sold = disposeForm.disposalMethod === 'SOLD';
      const res = await fixedAssetService.dispose(selectedAsset.id, {
        disposalMethod: disposeForm.disposalMethod,
        disposalDate: disposeForm.disposalDate,
        proceedsAmount: sold ? disposeForm.proceedsAmount : undefined,
        disposalAccountId: sold ? disposeForm.disposalAccountId : undefined,
        remarks: disposeForm.remarks.trim(),
      });
      const gl = parseFloat(res?.data?.gainLoss) || 0;
      setNotice(`${selectedAsset.name} disposed of — ${gl > 0 ? `gain AED ${money(gl)}` : gl < 0 ? `loss AED ${money(-gl)}` : 'no gain or loss'}`);
      setDisposeDialogOpen(false);
      setSelectedAsset(null);
      load();
    } catch (err) {
      setDisposeError(serverMessage(err, 'Failed to dispose of the asset'));
    } finally {
      setDisposing(false);
    }
  };

  const handleVoid = async () => {
    setVoiding(true);
    setVoidError(null);
    try {
      await fixedAssetService.voidAsset(selectedAsset.id, voidReason.trim());
      setNotice(`${selectedAsset.name} voided — the purchase was reversed`);
      setVoidOpen(false);
      setSelectedAsset(null);
      load();
    } catch (err) {
      setVoidError(serverMessage(err, 'Failed to void the asset'));
    } finally {
      setVoiding(false);
    }
  };

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Fixed Assets</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Coming soon">
            <span>
              <Button variant="text" size="small" startIcon={<FileDownload />} disabled sx={{ textTransform: 'none', fontSize: fs.xs }}>
                Export
              </Button>
            </span>
          </Tooltip>
          <Button variant="text" size="small" startIcon={<SettingsIcon />} onClick={() => navigate('/settings?section=finance&tab=fixed-assets')}
            sx={{ textTransform: 'none', fontSize: fs.xs }}>
            Settings
          </Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd}>
            Add Asset
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={openDepreciation}
              sx={{ textTransform: 'none' }}
            >
              Run Depreciation
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {/* Summary */}
      <SummaryCardGrid cards={summaryCards} loading={loading} />

      {/* Table */}
      <Box sx={{ mt: 1.5 }}>
        <DataTable columns={COLUMNS} data={assets} loading={loading} emptyMessage="No fixed assets registered."
          onRowClick={openDetail}
        />
      </Box>

      {/* Add Asset Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Add Fixed Asset</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {formError && <Alert severity="error" sx={{ mb: 1 }}>{formError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Asset Name" size="small" value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. HP LaserJet Printer"
            />
            <TextField
              label="Category" size="small" select value={form.category}
              onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            >
              {categories.map((c) => (
                <MenuItem key={c.key} value={c.key}>{c.label} ({c.usefulLifeYears} years)</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Purchase Date" type="date" size="small" value={form.purchaseDate}
              onChange={(e) => setForm((p) => ({ ...p, purchaseDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toYmd(new Date()) }}
            />
            <TextField
              label="Cost (AED)" type="number" size="small" value={form.cost}
              onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))}
              inputProps={{ min: 0, step: '0.01' }}
              error={belowThreshold}
              helperText={threshold != null
                ? (belowThreshold
                  ? `Not above the AED ${money(threshold)} threshold — record it as an expense instead`
                  : `Assets cost more than AED ${money(threshold)}`)
                : null}
            />
            <TextField
              label="Paid From" size="small" select value={form.paidFrom}
              onChange={(e) => setForm((p) => ({ ...p, paidFrom: e.target.value }))}
              helperText="The purchase is posted from this account or card"
            >
              {accounts.length > 0 && <ListSubheader>Bank and cash</ListSubheader>}
              {accounts.map((a) => (
                <MenuItem key={a.id} value={`account:${a.id}`}>{[a.bankName, a.accountName].filter(Boolean).join(' - ')}</MenuItem>
              ))}
              {cards.length > 0 && <ListSubheader>Credit cards</ListSubheader>}
              {cards.map((c) => (
                <MenuItem key={c.id} value={`card:${c.id}`}>{cardLabel(c)}{c.nickname ? ` - ${c.nickname}` : ''}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description" size="small" multiline rows={2} value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
            {previewMonthly && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Depreciation Preview</Typography>
                <Typography sx={{ fontSize: fs.sm }}>
                  AED {money(previewMonthly)} / month over {selectedCat.usefulLifeYears} years
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={handleSave}
            disabled={!form.name.trim() || !form.category || !form.cost || !form.paidFrom || belowThreshold || saving}
            sx={{ textTransform: 'none' }}>
            {saving ? 'Saving...' : 'Save Asset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Depreciation Dialog */}
      <Dialog open={depreciationDialogOpen} onClose={() => setDepreciationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Run Depreciation</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 1, flexWrap: 'wrap' }}>
            <TextField label="Up to" size="small" type="month" value={depMonth} sx={{ width: 170 }}
              InputLabelProps={{ shrink: true }} inputProps={{ max: latestDepreciationMonth() }}
              onChange={(e) => { if (e.target.value) { setDepMonth(e.target.value); loadPreview(e.target.value); } }} />
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', flex: 1 }}>
              Posts every month not yet depreciated, from each asset's purchase month up to {monthLabel(depMonth)}: one
              journal entry per month, dated its last day.
            </Typography>
          </Box>
          {previewError && <Alert severity="error" sx={{ mb: 1.5 }}>{previewError}</Alert>}
          {depreciationPreview?.alreadyPosted && (
            <Alert severity="info" sx={{ mb: 1.5 }}>Depreciation is already posted up to {monthLabel(depMonth)}.</Alert>
          )}
          {lockedMonths.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              The lock date covers {lockedMonths.map((m) => monthLabel(m.month)).join(', ')}. Move the lock date back
              (Settings › Finance › Lock date), run, then lock again.
            </Alert>
          )}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
            <Box sx={{ minWidth: 560 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px 120px', bgcolor: 'action.hover', p: 1 }}>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Asset</Typography>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textAlign: 'right' }}>Cost</Typography>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textAlign: 'right' }}>Months</Typography>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textAlign: 'right' }}>Depreciation</Typography>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textAlign: 'right' }}>Book Value After</Typography>
              </Box>
              {previewLoading && <Typography sx={{ fontSize: fs.xs, p: 1, color: 'text.secondary' }}>Loading…</Typography>}
              {!previewLoading && depreciationPreview && previewRows.length === 0 && (
                <Typography sx={{ fontSize: fs.xs, p: 1, color: 'text.secondary' }}>Nothing to depreciate up to {monthLabel(depMonth)}.</Typography>
              )}
              {previewRows.map((row) => (
                <Box key={row.assetId} sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px 120px', p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: fs.xs }}>{row.assetName}</Typography>
                  <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}>{money(row.cost)}</Typography>
                  <Tooltip title={`${monthLabel(row.fromMonth)} to ${monthLabel(row.toMonth)}`}>
                    <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}>{row.months}</Typography>
                  </Tooltip>
                  <Typography sx={{ fontSize: fs.xs, textAlign: 'right', fontWeight: fw.semibold }}>{money(row.depreciationAmount)}</Typography>
                  <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}>{money(row.bookAfter)}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px 120px', p: 1, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Total</Typography>
                <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}></Typography>
                <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}></Typography>
                <Typography sx={{ fontSize: fs.xs, textAlign: 'right', fontWeight: fw.semibold }}>
                  {money(depreciationPreview?.totalDepreciation)}
                </Typography>
                <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}></Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', mb: 0.5 }}>
              Journal Entry Preview ({previewMonths.length} monthly entr{previewMonths.length === 1 ? 'y' : 'ies'})
            </Typography>
            <Typography sx={{ fontSize: fs.xs }}>Dr: Depreciation Expense → P&L</Typography>
            <Typography sx={{ fontSize: fs.xs }}>Cr: Accumulated Depreciation, one line per category → reduces book value</Typography>
            {previewMonths.length > 0 && (
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>
                {previewMonths.length === 1 ? monthLabel(previewMonths[0].month)
                  : `${monthLabel(previewMonths[0].month)} to ${monthLabel(previewMonths[previewMonths.length - 1].month)}`}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setDepreciationDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            color="warning"
            disabled={depreciationRunning || previewLoading || !depreciationPreview || depreciationPreview.alreadyPosted
              || previewRows.length === 0 || lockedMonths.length > 0}
            onClick={postDepreciation}
            sx={{ textTransform: 'none' }}
          >
            {depreciationRunning ? 'Posting...' : 'Post Depreciation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asset Detail Dialog */}
      <Dialog open={Boolean(selectedAsset) && !disposeDialogOpen && !voidOpen} onClose={() => setSelectedAsset(null)} maxWidth="sm" fullWidth>
        {selectedAsset && (
          <>
            <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold, display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedAsset.name} <StatusChip status={selectedAsset.status} />
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
                <Fact label="Category">{categoryLabel(selectedAsset.category)} ({selectedAsset.usefulLifeYears} years)</Fact>
                <Fact label="Purchased">{formatDate(selectedAsset.purchaseDate)} · paid from {paidFromName(selectedAsset)}</Fact>
                <Fact label="Cost">AED {money(selectedAsset.cost)}</Fact>
                <Fact label="Monthly depreciation">AED {money(selectedAsset.monthlyDepreciation)}</Fact>
                <Fact label="Accumulated (posted)">AED {money(selectedAsset.accumulatedDepreciation)}</Fact>
                <Fact label="Book value">AED {money(selectedAsset.bookValue)}</Fact>
                {selectedAsset.fullyDepreciatedDate && <Fact label="Fully depreciated on">{formatDate(selectedAsset.fullyDepreciatedDate)}</Fact>}
                {selectedAsset.description && <Fact label="Description">{selectedAsset.description}</Fact>}
                {selectedAsset.status === 'DISPOSED' && (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <Fact label="Disposed">{formatDate(selectedAsset.disposalDate)} · {selectedAsset.disposalMethod === 'SOLD' ? 'Sold' : 'Scrapped'}</Fact>
                    {selectedAsset.disposalMethod === 'SOLD' && (
                      <Fact label="Proceeds">AED {money(selectedAsset.proceedsAmount)} into {accountName(selectedAsset.disposalAccountId)}</Fact>
                    )}
                    <Fact label={Number(selectedAsset.gainLoss) >= 0 ? 'Gain' : 'Loss'}>AED {money(Math.abs(Number(selectedAsset.gainLoss) || 0))}</Fact>
                    {selectedAsset.disposalRemarks && <Fact label="Remarks">{selectedAsset.disposalRemarks}</Fact>}
                  </>
                )}
                {selectedAsset.status === 'VOIDED' && (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <Fact label="Voided">{selectedAsset.voidReason || '—'}</Fact>
                  </>
                )}
              </Box>

              <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                Depreciation Schedule
              </Typography>
              {detailError && <Alert severity="error" sx={{ mb: 1 }}>{detailError}</Alert>}
              {!schedule && !detailError && <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Loading…</Typography>}
              {schedule && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 460 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '60px 60px 1fr 1fr 1fr 80px', bgcolor: 'action.hover', p: 1 }}>
                      {['Year', 'Months', 'Amount', 'Accumulated', 'Book Value', ''].map((h, i) => (
                        <Typography key={h || i} sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textAlign: i >= 2 && i <= 4 ? 'right' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                    {(schedule.schedule || []).map((s) => (
                      <Box key={s.year} sx={{ display: 'grid', gridTemplateColumns: '60px 60px 1fr 1fr 1fr 80px', p: 1, borderTop: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: fs.xs }}>{s.year}</Typography>
                        <Typography sx={{ fontSize: fs.xs }}>{s.months}</Typography>
                        <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}>{money(s.amount)}</Typography>
                        <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}>{money(s.accumulated)}</Typography>
                        <Typography sx={{ fontSize: fs.xs, textAlign: 'right' }}>{money(s.bookValue)}</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          {s.posted && <Chip label="Posted" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                          {!s.posted && s.postedMonths > 0 && (
                            <Chip label={`${s.postedMonths}/${s.months} posted`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {isAdmin && inUse && (
                <Button size="small" color="error" onClick={openDispose} sx={{ textTransform: 'none' }}>Dispose</Button>
              )}
              {canVoid && (
                <Button size="small" onClick={() => { setVoidReason(''); setVoidError(null); setVoidOpen(true); }} sx={{ textTransform: 'none' }}>
                  Void
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => setSelectedAsset(null)} sx={{ textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dispose Asset Dialog */}
      <Dialog open={disposeDialogOpen} onClose={() => setDisposeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>
          Dispose Asset: {selectedAsset?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {disposeError && <Alert severity="error" sx={{ mb: 1 }}>{disposeError}</Alert>}
          {selectedAsset && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography sx={{ fontSize: fs.xs }}>Cost: AED {money(selectedAsset.cost)}</Typography>
              <Typography sx={{ fontSize: fs.xs }}>Accumulated Depreciation: AED {money(selectedAsset.accumulatedDepreciation)}</Typography>
              <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Book Value: AED {money(selectedAsset.bookValue)}</Typography>
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.5 }}>
                The cost less the depreciation posted so far — months since the last run are not posted.
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Disposal Method" size="small" select value={disposeForm.disposalMethod}
              onChange={(e) => setDisposeForm((p) => ({ ...p, disposalMethod: e.target.value, proceedsAmount: '', disposalAccountId: '' }))}
            >
              <MenuItem value="SOLD">Sold (received proceeds)</MenuItem>
              <MenuItem value="SCRAPPED">Scrapped (no proceeds)</MenuItem>
            </TextField>
            <TextField
              label="Disposal Date" type="date" size="small" value={disposeForm.disposalDate}
              onChange={(e) => setDisposeForm((p) => ({ ...p, disposalDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toYmd(new Date()), min: selectedAsset?.purchaseDate }}
            />
            {disposeForm.disposalMethod === 'SOLD' && (
              <>
                <TextField
                  label="Proceeds Amount (AED)" type="number" size="small" value={disposeForm.proceedsAmount}
                  onChange={(e) => setDisposeForm((p) => ({ ...p, proceedsAmount: e.target.value }))}
                  inputProps={{ min: 0, step: '0.01' }}
                />
                <TextField
                  label="Received Into" size="small" select value={disposeForm.disposalAccountId}
                  onChange={(e) => setDisposeForm((p) => ({ ...p, disposalAccountId: e.target.value }))}
                >
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>{[a.bankName, a.accountName].filter(Boolean).join(' - ')}</MenuItem>
                  ))}
                </TextField>
              </>
            )}
            <TextField
              label="Remarks" size="small" multiline rows={2} value={disposeForm.remarks}
              onChange={(e) => setDisposeForm((p) => ({ ...p, remarks: e.target.value }))}
            />
            {selectedAsset && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', mb: 0.5 }}>Journal Entry Preview</Typography>
                {(() => {
                  const bookVal = parseFloat(selectedAsset.bookValue) || 0;
                  const proceeds = disposeForm.disposalMethod === 'SOLD' ? (parseFloat(disposeForm.proceedsAmount) || 0) : 0;
                  const accDep = parseFloat(selectedAsset.accumulatedDepreciation) || 0;
                  const gainLoss = proceeds - bookVal;
                  return (
                    <>
                      {proceeds > 0 && <Typography sx={{ fontSize: fs.xs }}>Dr: Bank/Cash — AED {money(proceeds)}</Typography>}
                      <Typography sx={{ fontSize: fs.xs }}>Dr: Accumulated Depreciation — AED {money(accDep)}</Typography>
                      {gainLoss < 0 && <Typography sx={{ fontSize: fs.xs, color: 'error.main' }}>Dr: Loss on Disposal — AED {money(Math.abs(gainLoss))}</Typography>}
                      <Typography sx={{ fontSize: fs.xs }}>Cr: Fixed Asset (Cost) — AED {money(selectedAsset.cost)}</Typography>
                      {gainLoss > 0 && <Typography sx={{ fontSize: fs.xs, color: 'success.main' }}>Cr: Gain on Disposal — AED {money(gainLoss)}</Typography>}
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setDisposeDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            disabled={disposing || !disposeForm.disposalDate
              || (disposeForm.disposalMethod === 'SOLD' && (!(parseFloat(disposeForm.proceedsAmount) > 0) || !disposeForm.disposalAccountId))}
            onClick={handleDispose}
            sx={{ textTransform: 'none' }}
          >
            {disposing ? 'Processing...' : 'Dispose Asset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Asset Dialog */}
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Void Asset: {selectedAsset?.name}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {voidError && <Alert severity="error" sx={{ mb: 1 }}>{voidError}</Alert>}
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 1.5 }}>
            For an asset recorded by mistake. The purchase entry is reversed (dated today) and the money goes back to
            {selectedAsset ? ` ${paidFromName(selectedAsset)}` : ' where it came from'}.
          </Typography>
          <TextField label="Reason" size="small" fullWidth multiline rows={2} autoFocus value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setVoidOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" size="small" color="error" disabled={voiding || !voidReason.trim()} onClick={handleVoid}
            sx={{ textTransform: 'none' }}>
            {voiding ? 'Voiding...' : 'Void Asset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssets;
